import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { compacto } from '../utils/numero';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

// Estado de la firma Authenticode del destino de una exclusión. `hash_mismatch` es el
// caso grave: binario firmado por el fabricante y alterado después de firmar.
const SIG_MAP = {
  valid:          { label: 'Firma válida',   color: 'var(--ink-2)' },
  unsigned:       { label: 'Sin firma',      color: '#D45D00' },
  hash_mismatch:  { label: 'Hash alterado',  color: 'var(--red)' },
  untrusted_root: { label: 'Raíz no confiable', color: 'var(--red)' },
  expired:        { label: 'Cert. vencido',  color: '#D45D00' },
  revoked:        { label: 'Cert. revocado', color: 'var(--red)' },
  distrust:       { label: 'Desconfiado',    color: 'var(--red)' },
  error:          { label: '—',              color: 'var(--gray-500)' },
};

const KIND_LABEL = { path: 'Ruta', extension: 'Extensión', process: 'Proceso', ip: 'IP' };

// Días de atraso a partir de los cuales la firma se considera vencida (coincide con el
// umbral del backend). Un equipo sano actualiza a diario.
function edadFirma(d) {
  if (d == null) return { txt: '—', color: 'var(--gray-500)' };
  // compacto(): un equipo abandonado puede pasar los 1000 días y desbordar la celda.
  const txt = `${compacto(d)} d`;
  if (d > 30) return { txt, color: 'var(--red)', exacto: d };
  if (d > 7)  return { txt, color: '#D45D00', exacto: d };
  return { txt, color: 'var(--ink-2)' };
}

function Bool({ v, invert }) {
  if (v == null) return <span style={{ color: 'var(--gray-500)' }}>—</span>;
  const malo = invert ? v : !v;
  return <span style={{ color: malo ? 'var(--red)' : 'var(--ink-2)', fontSize: 12 }}>{v ? 'Sí' : 'No'}</span>;
}

export default function AntivirusPosture() {
  const [summary, setSummary] = useState(null);
  const [exclusions, setExclusions] = useState([]);
  const [assets, setAssets] = useState([]);
  const [view, setView] = useState('exclusiones');
  const [soloSospechosas, setSoloSospechosas] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState({});

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = () => {
    setLoading(true);
    Promise.all([
      fetch(`${API_URL}/antivirus/summary`, { headers }).then(r => r.json()),
      fetch(`${API_URL}/antivirus/exclusions`, { headers }).then(r => r.json()),
      fetch(`${API_URL}/antivirus/assets`, { headers }).then(r => r.json()),
    ]).then(([s, e, a]) => {
      setSummary(s && !s.error ? s : null);
      setExclusions(Array.isArray(e) ? e : []);
      setAssets(Array.isArray(a) ? a : []);
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(load, []);   // eslint-disable-line react-hooks/exhaustive-deps

  const aprobar = async (ex, aprobar_) => {
    setBusy(b => ({ ...b, [ex.id]: true }));
    try {
      await fetch(`${API_URL}/antivirus/exclusions/${ex.id}/approve`, {
        method: aprobar_ ? 'POST' : 'DELETE',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: aprobar_ ? JSON.stringify({}) : undefined,
      });
      setExclusions(list => list.map(x => x.id === ex.id ? { ...x, approved: aprobar_ } : x));
    } finally {
      setBusy(b => ({ ...b, [ex.id]: false }));
    }
  };

  // Quitar la exclusión en el equipo. Para las huérfanas es inocuo — el destino no
  // existe— así que va directo. Si el archivo existe, el backend devuelve 409 pidiendo
  // confirmación explícita, porque ahí el antivirus puede poner el binario en cuarentena
  // y dejar sin activar lo que dependa de él.
  const quitar = async (ex, confirmado = false) => {
    setBusy(b => ({ ...b, [ex.id]: true }));
    try {
      const r = await fetch(`${API_URL}/antivirus/exclusions/${ex.id}/remove`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(confirmado ? { confirmar: true } : {}),
      });
      const j = await r.json();
      if (r.status === 409 && j.requiere_confirmacion) {
        if (window.confirm(`${j.error}

${ex.value}

¿Quitarla igual?`)) {
          setBusy(b => ({ ...b, [ex.id]: false }));
          return quitar(ex, true);
        }
        return;
      }
      if (!r.ok) { window.alert(j.error || `El servidor respondió ${r.status}`); return; }
      setExclusions(list => list.map(x =>
        x.id === ex.id ? { ...x, removal_requested_at: new Date().toISOString() } : x));
    } catch (e) {
      window.alert('No se pudo encolar la quita: ' + e.message);
    } finally {
      setBusy(b => ({ ...b, [ex.id]: false }));
    }
  };

  const visibles = soloSospechosas
    ? exclusions.filter(e => e.suspicious && !e.approved)
    : exclusions;

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--gray-900)' }}>Postura de Antivirus</div>
          <div style={{ fontSize: 13, color: 'var(--gray-500)' }}>
            Cómo está configurado el antivirus activo de cada equipo: firmas, escaneos y exclusiones
          </div>
        </div>
        <button className="btn btn-secondary" onClick={load} disabled={loading}>
          {loading ? 'Cargando...' : 'Actualizar'}
        </button>
      </div>

      {summary && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 20 }}>
          {[
            ['Exclusiones sospechosas', summary.exclusiones?.sospechosas, 'var(--red)'],
            [`Firmas de más de ${summary.umbrales?.firma_dias ?? 7} días`, summary.firmas_viejas, '#D45D00'],
            ['Sin Tamper Protection', summary.sin_tamper, '#D45D00'],
            ['Sin full scan nunca', summary.full_scan_nunca, 'var(--ink-2)'],
          ].map(([label, val, color]) => (
            <div key={label} className="card" style={{ padding: '14px 16px' }}>
              <div style={{ fontSize: 11, color: 'var(--gray-500)', marginBottom: 4 }}>{label}</div>
              <div style={{ fontSize: 26, fontWeight: 700, color }}>{val || 0}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div style={{ display: 'flex', gap: 8, padding: '12px 16px', borderBottom: '1px solid var(--gray-100)', flexWrap: 'wrap', alignItems: 'center' }}>
          {[
            { key: 'exclusiones', label: 'Exclusiones' },
            { key: 'equipos',     label: 'Por equipo' },
          ].map(v => (
            <button key={v.key} className={`btn ${view === v.key ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setView(v.key)}>{v.label}</button>
          ))}
          {view === 'exclusiones' && (
            <label style={{ marginLeft: 8, fontSize: 12, color: 'var(--ink-2)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={soloSospechosas} onChange={e => setSoloSospechosas(e.target.checked)} />
              Solo sospechosas sin aprobar
            </label>
          )}
        </div>

        {loading && <div style={{ padding: 24, fontSize: 13, color: 'var(--gray-500)' }}>Cargando...</div>}

        {!loading && view === 'exclusiones' && (
          visibles.length === 0 ? (
            <div style={{ padding: 24, fontSize: 13, color: 'var(--gray-500)' }}>
              {soloSospechosas
                ? 'No hay exclusiones sospechosas sin revisar.'
                : 'Todavía no se registraron exclusiones. Requiere agente 2.7.1 o superior.'}
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Equipo</th><th>Tipo</th><th>Exclusión</th><th>Firma</th><th>Motivo</th><th></th>
                </tr>
              </thead>
              <tbody>
                {visibles.map(e => {
                  const sig = SIG_MAP[e.signature];
                  return (
                    <tr key={e.id} style={e.removed_at ? { opacity: 0.5 } : undefined}>
                      <td>
                        <Link to={`/assets/${e.asset_id}`} style={{ color: 'var(--accent)' }}>{e.hostname}</Link>
                        {e.location && <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{e.location}</div>}
                      </td>
                      <td style={{ fontSize: 12, color: 'var(--ink-2)' }}>{KIND_LABEL[e.kind] || e.kind}</td>
                      <td style={{ fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all', maxWidth: 320 }}>
                        {e.value}
                        {e.target_exists === false && (
                          <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>la ruta ya no existe</div>
                        )}
                      </td>
                      <td style={{ fontSize: 11, color: sig ? sig.color : 'var(--gray-500)' }}>
                        {sig ? sig.label : '—'}
                        {e.signer && <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>{e.signer}</div>}
                      </td>
                      <td style={{ fontSize: 11, color: e.suspicious ? 'var(--red)' : 'var(--gray-500)' }}>
                        {e.reason || '—'}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        {e.removal_requested_at ? (
                          <span style={{ fontSize: 11, color: 'var(--gray-500)' }}>⏳ quitándose</span>
                        ) : (<>
                        {/* Quitar solo tiene sentido mientras siga configurada. */}
                        {!e.removed_at && (
                          <button className="btn btn-secondary" disabled={busy[e.id]}
                            title={e.target_exists === false
                              ? 'La ruta ya no existe: quitarla no afecta a nada en ejecución'
                              : 'El archivo existe — se va a pedir confirmación'}
                            onClick={() => quitar(e)} style={{ marginRight: 6 }}>Quitar</button>
                        )}
                        {e.approved ? (
                          <button className="btn btn-secondary" disabled={busy[e.id]}
                            onClick={() => aprobar(e, false)}>Quitar aprobación</button>
                        ) : (
                          <button className="btn btn-secondary" disabled={busy[e.id]}
                            title="Marcar como exclusión legítima: deja de contar como sospechosa"
                            onClick={() => aprobar(e, true)}>Aprobar</button>
                        )}
                        </>)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        )}

        {!loading && view === 'equipos' && (
          assets.length === 0 ? (
            <div style={{ padding: 24, fontSize: 13, color: 'var(--gray-500)' }}>Sin datos de postura todavía.</div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Equipo</th><th>Antivirus</th><th>Protección</th><th>Tamper</th>
                  <th>Firmas</th><th>Quick scan</th><th>Full scan</th><th>Exclusiones</th>
                </tr>
              </thead>
              <tbody>
                {assets.map(a => {
                  const f = edadFirma(a.defender_signature_age_days);
                  return (
                    <tr key={a.id}>
                      <td>
                        <Link to={`/assets/${a.id}`} style={{ color: 'var(--accent)' }}>{a.hostname}</Link>
                        {a.location && <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{a.location}</div>}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {a.antivirus_name || '—'}
                        {a.antivirus_vendor === 'trendmicro' && (
                          <div style={{ fontSize: 10, color: 'var(--gray-500)' }}>sin tamper legible</div>
                        )}
                      </td>
                      <td><Bool v={a.defender_enabled} /></td>
                      <td><Bool v={a.defender_tamper_protected} /></td>
                      <td style={{ fontSize: 12, color: f.color, fontWeight: f.color === 'var(--red)' ? 600 : 400 }}
                          title={f.exacto != null ? `${f.exacto} días` : undefined}>{f.txt}</td>
                      <td style={{ fontSize: 12 }}>
                        {a.defender_quick_scan_age_days == null ? 'nunca' : `${compacto(a.defender_quick_scan_age_days)} d`}
                      </td>
                      <td style={{ fontSize: 12, color: a.defender_full_scan_age_days == null ? '#D45D00' : 'var(--ink-2)' }}>
                        {a.defender_full_scan_age_days == null ? 'nunca' : `${compacto(a.defender_full_scan_age_days)} d`}
                      </td>
                      <td style={{ fontSize: 12 }}>
                        {a.exclusiones || 0}
                        {a.exclusiones_sospechosas > 0 && (
                          <span style={{ color: 'var(--red)', fontWeight: 600 }}> ({a.exclusiones_sospechosas} sosp.)</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )
        )}
      </div>

      <div style={{ fontSize: 11, color: 'var(--gray-500)', marginTop: 12 }}>
        Los datos los reporta el agente en cada inventario, del antivirus ACTIVO de cada equipo. En Apex One las exclusiones las administra el servidor, no el equipo: las que aparecen acá son las de Windows Defender, que queda instalado en modo pasivo.
      </div>
    </div>
  );
}
